import { Routes, Route } from 'react-router-dom';
import { SystemRole } from '@peles/shared';
import { HomePage } from '../pages/HomePage';
import { LoginPage } from '../pages/LoginPage';
import { GoogleCallbackPage } from '../pages/GoogleCallbackPage';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import { UsersPage } from '../pages/UsersPage';
import { EventsPage } from '../pages/EventsPage';
import { EventDetailPage } from '../pages/EventDetailPage';
import { VenueDetailPage } from '../pages/VenueDetailPage';
import { VendorDetailPage } from '../pages/VendorDetailPage';
import { TemplatesPage } from '../pages/TemplatesPage';
import { TimelineTemplateEditPage } from '../pages/TimelineTemplateEditPage';
import { TimelineEditPage } from '../pages/TimelineEditPage';
import { VenuesPage } from '../pages/VenuesPage';
import { VendorsPage } from '../pages/VendorsPage';
import { ExpensesPage } from '../pages/ExpensesPage';
import { TimelinePage } from '../pages/TimelinePage';
import { TaskListPage } from '../pages/TaskListPage';
import { KibuddimPage } from '../pages/KibuddimPage';
import { GuestListPage } from '../pages/GuestListPage';
import { FloorPlanPage } from '../pages/FloorPlanPage';
import { PhotosPage } from '../pages/PhotosPage';
import { ProfilePage } from '../pages/ProfilePage';
import { AdminSqlClientPage } from '../pages/AdminSqlClientPage';
import { SettingsPage } from '../pages/SettingsPage';
import { ClientDashboardPage } from '../pages/ClientDashboardPage';
import { ClientEventDetailPage } from '../pages/ClientEventDetailPage';
import { VendorDashboardPage } from '../pages/VendorDashboardPage';
import { InvitationsPage } from '../pages/InvitationsPage';
import { ReportsPage } from '../pages/ReportsPage';
import { EmailLogsPage } from '../pages/EmailLogsPage';
import { DataImportPage } from '../pages/DataImportPage';
import { CalendarPage } from '../pages/CalendarPage';
import { InquiriesPage } from '../pages/InquiriesPage';
import { LandingPage } from '../pages/LandingPage';
import { FilesPage } from '../pages/FilesPage';
import { QuestionnairesPage } from '../pages/QuestionnairesPage';
import { QuestionnaireDetailPage } from '../pages/QuestionnaireDetailPage';
import { PublicQuestionnairePage } from '../pages/PublicQuestionnairePage';
import { CommissionsPage } from '../pages/CommissionsPage';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { AppLayout, PublicLayout } from '../components/layout';

/**
 * Determine if the current hostname should render the public marketing site.
 * Production: np-productions.com → public, myevent.np-productions.com → app
 * Test: public-test.np-productions.com → public, test.np-productions.com → app
 * Local: localhost with ?public=true → public, otherwise → app
 */
export function isPublicSite(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  if (
    hostname === 'np-productions.com' ||
    hostname === 'www.np-productions.com' ||
    hostname === 'public-test.np-productions.com'
  ) {
    return true;
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    return params.get('public') === 'true';
  }
  return false;
}

export function AppRouter() {
  if (isPublicSite()) {
    return (
      <Routes>
        <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
        <Route element={<PublicLayout />}>
          <Route index element={<LandingPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />

        {/* Client Portal */}
        <Route
          path="client"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="client/events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <ClientEventDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Vendor Portal */}
        <Route
          path="vendor"
          element={
            <ProtectedRoute minimumRole={SystemRole.VENDOR}>
              <VendorDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Invitations (PLANNER+) */}
        <Route
          path="invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/invitations"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InvitationsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="users"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="calendar"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <EventsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <EventDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="venues"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenuesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="venues/:venueId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VenueDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="vendors/:vendorId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <VendorDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="expenses"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="commissions"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <CommissionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/expenses"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="timelines"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/timeline"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/timeline/:timelineId"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TimelineEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="checklists"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/checklist"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <TaskListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="kibuddim"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <KibuddimPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/kibuddim"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <KibuddimPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="guests"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <GuestListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/guests"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <GuestListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="floor-plans"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <FloorPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/floor-plan"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <FloorPlanPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="photos"
          element={
            <ProtectedRoute minimumRole={SystemRole.CLIENT}>
              <PhotosPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/questionnaires"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnairesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="events/:eventId/questionnaires/:questionnaireId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnaireDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/questionnaire/:questionnaireId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <QuestionnaireDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates/timeline/:templateId"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <TimelineTemplateEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="reports"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="files"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <FilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="import"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <DataImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/email-logs"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <EmailLogsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/inquiries"
          element={
            <ProtectedRoute minimumRole={SystemRole.PLANNER}>
              <InquiriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="admin/sql-client"
          element={
            <ProtectedRoute minimumRole={SystemRole.ADMIN}>
              <AdminSqlClientPage />
            </ProtectedRoute>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
      </Route>
    </Routes>
  );
}
